import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Seo } from '@/components/Seo';
import { ICONS, Icon, InfoShell } from './InfoShell';

// New-design legal page (Privacy Policy, Terms of Service): numbered
// sections with a sticky contents list that follows the reader. Text comes
// from the classic keys: <prefix>.sectionN.title / .body, <prefix>.hero.*,
// <prefix>.lastUpdated.

export default function LegalV2({ prefix, count, pageKey }: { prefix: 'privacy' | 'terms'; count: number; pageKey: string }) {
  const { t } = useTranslation();
  const [active, setActive] = useState(1);
  const sections = Array.from({ length: count }, (_, i) => ({
    n: i + 1,
    title: t(`${prefix}.section${i + 1}.title`).replace(/^\s*\d+[.)]\s*/, ''),
    body: t(`${prefix}.section${i + 1}.body`),
  }));

  useEffect(() => {
    const io = new IntersectionObserver(
      (es) => {
        const vis = es.filter((e) => e.isIntersecting).sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top)[0];
        if (vis) setActive(Number((vis.target as HTMLElement).dataset.n));
      },
      { rootMargin: '-20% 0px -65% 0px' },
    );
    document.querySelectorAll('.lsec[data-n]').forEach((el) => io.observe(el));
    return () => io.disconnect();
  }, [count]);

  const go = (n: number) => document.getElementById(`${prefix}-${n}`)?.scrollIntoView({ behavior: 'smooth', block: 'start' });

  return (
    <>
      <Seo pageKey={pageKey} />
      <InfoShell
        label={t(`${prefix}.hero.label`)}
        title={t(`${prefix}.hero.heading`)}
        lede={t('infoV2.legal.lede', 'Please read this carefully. If anything is unclear, our team is happy to explain.')}
        meta={
          <div className="lmeta">
            <span>
              <Icon d={ICONS.clock} />
              {t(`${prefix}.lastUpdated`)}
            </span>
            <span>
              <Icon d={ICONS.doc} />
              {t('infoV2.legal.sections', '{{n}} sections', { n: count })}
            </span>
          </div>
        }
      >
        <section className="isec">
          <div className="wrap llay">
            <aside className="ltoc">
              <span className="lbl">{t('infoV2.legal.contents', 'Contents')}</span>
              <ol>
                {sections.map((s) => (
                  <li key={s.n} className={active === s.n ? 'on' : ''}>
                    <a
                      href={`#${prefix}-${s.n}`}
                      onClick={(e) => {
                        e.preventDefault();
                        go(s.n);
                      }}
                    >
                      <em>{String(s.n).padStart(2, '0')}</em>
                      {s.title}
                    </a>
                  </li>
                ))}
              </ol>
            </aside>
            <div className="lbody">
              {sections.map((s) => (
                <article key={s.n} id={`${prefix}-${s.n}`} data-n={s.n} className="lsec">
                  <span className="ln">{String(s.n).padStart(2, '0')}</span>
                  <div>
                    <h2>{s.title}</h2>
                    <p>{s.body}</p>
                  </div>
                </article>
              ))}
            </div>
          </div>
        </section>
      </InfoShell>
    </>
  );
}
